import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, auth } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select, Stat } from "../components/UI";
import { toast } from "sonner";
import { Vote, Users2, Flag, RefreshCcw, Loader2, CheckCircle2, XCircle, Crown, MapPin } from "lucide-react";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR","VI","GU"];
const REGIONS = ["Northeast", "Midwest", "South", "West", "Territories"];

export const PARTY_COLORS = {
  Democratic: "text-sky-300 bg-sky-500/10 border-sky-400/30",
  Republican: "text-red-300 bg-red-500/10 border-red-400/30",
  Independent: "text-amber-300 bg-amber-500/10 border-amber-400/30",
  Libertarian: "text-yellow-300 bg-yellow-500/10 border-yellow-400/30",
  Green: "text-emerald-300 bg-emerald-500/10 border-emerald-400/30",
  Other: "text-slate-300 bg-white/5 border-white/20",
};

export const PartyBadge = ({ party }) => (
  <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${PARTY_COLORS[party] || PARTY_COLORS.Other}`}>{party}</span>
);

export default function CandidateDirectory() {
  const navigate = useNavigate();
  const user = auth.getUser();
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [mine, setMine] = useState(null);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [f, setF] = useState({ region: "", state: "", office: "", party: "", search: "" });

  const load = async () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    try {
      const [s, c, m] = await Promise.all([
        api.get("/midterms/stats"),
        api.get("/midterms/candidates", { params }),
        api.get("/midterms/my-candidate"),
      ]);
      setStats(s.data); setCandidates(c.data); setMine(m.data);
      if (isAdmin) api.get("/midterms/claims", { params: { status: "pending" } }).then(r => setClaims(r.data)).catch(() => {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [JSON.stringify(f)]); // eslint-disable-line

  const syncFec = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/midterms/sync-fec");
      toast.success(`FEC sync: ${r.data.upserted} candidates updated (${r.data.total_in_db} total)`);
      if (r.data.errors?.length) toast.warning(r.data.errors[0]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "FEC sync failed");
    } finally { setSyncing(false); }
  };

  const reviewClaim = async (id, action) => {
    try {
      await api.post(`/midterms/claims/${id}/${action}`);
      toast.success(`Claim ${action}d`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const races = {};
  candidates.forEach(c => { (races[c.race_id] = races[c.race_id] || []).push(c); });

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="2026 Midterms · Live FEC Data"
        title="Candidate Directory"
        subtitle="Every declared 2026 midterm candidate by region and state. Claim your page, track your race, and get AI-powered strategy."
        action={isAdmin && (
          <Btn variant="secondary" onClick={syncFec} disabled={syncing} data-testid="fec-sync-btn">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />} Sync FEC
          </Btn>
        )}
      />

      {mine?.claimed && (
        <Card className="border-cyan-400/30" data-testid="my-candidate-banner">
          <div className="flex flex-wrap items-center gap-3">
            <Crown className="w-6 h-6 text-cyan-300 shrink-0" />
            <div className="flex-1 min-w-[220px]">
              <div className="font-display font-bold">Your claimed page: {mine.candidate.name}</div>
              <div className="text-xs text-slate-400">{mine.candidate.office_full} · {mine.candidate.state}{mine.candidate.office === "H" && `-${mine.candidate.district}`} · {mine.candidate.party}</div>
              {mine.kit && <div className="text-[11px] text-emerald-300 mt-1">✓ Campaign kit ready — entity, fundraiser, landing page & live microsite auto-provisioned</div>}
            </div>
            <div className="flex flex-wrap gap-2">
              {mine.kit?.microsite_slug && (
                <Btn variant="secondary" onClick={() => window.open(`/m/${mine.kit.microsite_slug}`, "_blank")} data-testid="kit-microsite-btn">View microsite</Btn>
              )}
              {mine.kit && <Btn variant="secondary" onClick={() => navigate("/fundraising")} data-testid="kit-fundraising-btn">Fundraising HQ</Btn>}
              <Btn onClick={() => navigate(`/midterms/race/${mine.candidate.race_id}`)} data-testid="open-my-race-btn">Open my race</Btn>
            </div>
          </div>
        </Card>
      )}
      {mine && !mine.claimed && mine.pending_claim && (
        <Card className="border-amber-400/30">
          <div className="text-sm text-amber-300">Your claim for “{mine.pending_claim.candidate_name}” is pending admin review.</div>
        </Card>
      )}

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Candidates" value={stats.total_candidates.toLocaleString()} icon={Users2} testid="stat-candidates" />
          <Stat label="Races tracked" value={stats.total_races} icon={Vote} testid="stat-races" />
          <Stat label="Claimed pages" value={stats.claimed_pages} icon={Flag} testid="stat-claimed" />
          <Stat label="Regions" value={Object.keys(stats.by_region || {}).length} icon={MapPin} testid="stat-regions" />
        </div>
      )}

      {isAdmin && claims.length > 0 && (
        <Card data-testid="claims-review-panel">
          <h3 className="font-display text-lg font-bold mb-3">Pending page claims ({claims.length})</h3>
          <div className="space-y-2">
            {claims.map(cl => (
              <div key={cl.id} className="flex flex-wrap items-center gap-3 border-b border-white/5 py-2 text-sm">
                <div className="flex-1 min-w-[200px]">
                  <div className="text-white font-medium">{cl.candidate_name}</div>
                  <div className="text-xs text-slate-500">by {cl.user_email} · official: {cl.official_email} {cl.fec_id_confirmation && `· FEC ${cl.fec_id_confirmation}`}</div>
                  {cl.message && <div className="text-xs text-slate-400 mt-0.5">“{cl.message}”</div>}
                </div>
                <Btn variant="secondary" onClick={() => reviewClaim(cl.id, "approve")} data-testid={`approve-claim-${cl.id}`}><CheckCircle2 className="w-4 h-4 text-emerald-300" /> Approve</Btn>
                <Btn variant="danger" onClick={() => reviewClaim(cl.id, "reject")} data-testid={`reject-claim-${cl.id}`}><XCircle className="w-4 h-4" /> Reject</Btn>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="flex flex-wrap gap-2 mb-4">
          {["", ...REGIONS].map(r => (
            <button key={r || "all"} onClick={() => setF({ ...f, region: r, state: "" })} data-testid={`region-tab-${r || "all"}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${f.region === r ? "bg-cyan-400/15 border-cyan-400/40 text-cyan-300" : "border-white/10 text-slate-400 hover:text-white"}`}>
              {r || "All regions"}{stats && r && ` · ${stats.by_region?.[r] || 0}`}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <Select label="State" value={f.state} onChange={e => setF({ ...f, state: e.target.value })} data-testid="state-filter">
            <option value="">All states</option>
            {US_STATES.map(s => <option key={s}>{s}</option>)}
          </Select>
          <Select label="Office" value={f.office} onChange={e => setF({ ...f, office: e.target.value })} data-testid="office-filter">
            <option value="">Senate + House</option>
            <option value="S">Senate</option>
            <option value="H">House</option>
          </Select>
          <Select label="Party" value={f.party} onChange={e => setF({ ...f, party: e.target.value })} data-testid="party-filter">
            <option value="">All parties</option>
            {Object.keys(PARTY_COLORS).map(p => <option key={p}>{p}</option>)}
          </Select>
          <Input label="Search name" value={f.search} onChange={e => setF({ ...f, search: e.target.value })} placeholder="e.g. Ossoff" className="md:col-span-2" data-testid="candidate-search" />
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2 text-cyan-300" />Loading candidates…</div>
        ) : (
          <div className="space-y-4" data-testid="races-list">
            {Object.entries(races).map(([raceId, list]) => {
              const first = list[0];
              return (
                <div key={raceId} className="border border-white/10 rounded-lg overflow-hidden hover:border-cyan-400/30 transition-colors cursor-pointer" onClick={() => navigate(`/midterms/race/${raceId}`)} data-testid={`race-${raceId}`}>
                  <div className="px-4 py-2.5 bg-white/[0.03] flex items-center justify-between">
                    <div className="font-display font-bold text-sm">
                      {first.office_full} · {first.state}{first.office === "H" && `-${first.district}`}
                      <span className="ml-2 text-xs font-normal text-slate-500">{first.region} · {list.length} candidate{list.length > 1 ? "s" : ""}</span>
                    </div>
                    <span className="text-xs text-cyan-300">View race →</span>
                  </div>
                  <div className="px-4 py-2 flex flex-wrap gap-2">
                    {list.slice(0, 6).map(c => (
                      <div key={c.id} className="flex items-center gap-1.5 text-xs text-slate-300 bg-white/[0.03] rounded-full px-2.5 py-1">
                        <span className="truncate max-w-[180px]">{c.name}</span>
                        <PartyBadge party={c.party} />
                        {(c.incumbent_challenge || "").toLowerCase() === "incumbent" && <span className="text-[9px] text-cyan-300 uppercase">INC</span>}
                        {c.claim_status === "approved" && <Crown className="w-3 h-3 text-cyan-300" title="Claimed" />}
                      </div>
                    ))}
                    {list.length > 6 && <span className="text-xs text-slate-500 self-center">+{list.length - 6} more</span>}
                  </div>
                </div>
              );
            })}
            {Object.keys(races).length === 0 && <div className="py-10 text-center text-sm text-slate-500">No candidates match these filters.{isAdmin && " Run “Sync FEC” to pull the roster."}</div>}
          </div>
        )}
      </Card>
    </div>
  );
}
